import { Fragment, ReactNode } from "react";

import { PrismicRichText } from "@prismicio/react";
import { KeyTextField, RichTextField } from "@prismicio/types";

import { isRichText, useInterpolate } from "utils/prismic-utils";

type ContentProps = {
  field: KeyTextField | RichTextField;
};

export default function Content({ field }: ContentProps) {
  const interpolate = useInterpolate();

  if (!field) {
    return null;
  }

  /**
   * Some manual work needed to interpolate values on a per-line basis for
   * multi-line rich text fields
   *
   * Based on code from [the Prismic docs](https://prismic.io/docs/technical-reference/prismicio-react#prismicrichtext)
   */
  function createReactNodeArrayFromNewlines({ text }: { text: string }) {
    const result: ReactNode[] = [];
    text.split("\n").forEach((line, i) => {
      if (i > 0) {
        result.push(<br key={`${i}__break`} />);
      }
      result.push(<Fragment key={`${i}__line`}>{interpolate(line)}</Fragment>);
    });
    return result;
  }

  if (isRichText(field)) {
    /**
     *  RichTextFields consist of an array of elements and values.
     *
     * Use the PrismicRichText component to deal with how to render it,
     * but intercept text to interpolate values
     */
    return (
      <PrismicRichText
        field={field}
        components={{
          span: ({ key, text }) => (
            <span key={key}>{createReactNodeArrayFromNewlines({ text })}</span>
          ),
          strong: ({ key, text }) => (
            <strong key={key}>
              {createReactNodeArrayFromNewlines({ text })}
            </strong>
          ),
          em: ({ key, text }) => (
            <em key={key}>{createReactNodeArrayFromNewlines({ text })}</em>
          ),
        }}
      />
    );
  }

  // Just a plain string, return a `ReactNode` for consistency.
  return <>{interpolate(field)}</>;
}
